import { Typography } from '@mui/joy';
import { Control, UseFormGetValues, UseFormHandleSubmit } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation, FormField, Notification } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { IConfirmation } from 'core/types';
import { NOTE_DEFAULT_MAX_LENGTH } from 'quotes/constants';
import { QuoteEntityType, QuoteNoteType, QuoteType } from 'quotes/enums';
import { IQuoteNoteForm } from 'quotes/types';
import { Level } from 'theme/enums';

interface IProps {
  control: Control<IQuoteNoteForm>;
  getValues: UseFormGetValues<IQuoteNoteForm>;
  handleSubmit: UseFormHandleSubmit<IQuoteNoteForm>;
  onSubmit: (data: IQuoteNoteForm) => void;
  type: QuoteNoteType;
  submitEnabled: boolean;
  confirmationOption?: IConfirmation;
  disabledFields?: boolean;
  entityType?: QuoteEntityType;
  isAlternateEnabled?: boolean;
  quoteType?: QuoteType;
}

export const QuoteNoteForm = ({
  control,
  getValues,
  handleSubmit,
  onSubmit,
  type,
  submitEnabled,
  confirmationOption,
  disabledFields = false,
  entityType = QuoteEntityType.QUOTE,
  isAlternateEnabled = false,
  quoteType = QuoteType.CUSTOMER,
}: IProps) => {
  const { t } = useTranslation();

  const getTabTitle = () => {
    switch (type) {
      case QuoteNoteType.INTERNAL_NOTE:
        return t('labelQuoteInternalNote');
      case QuoteNoteType.CUSTOMER_NOTE:
        return QuoteType.CUSTOMER === quoteType ? t('labelQuoteCustomerNote') : t('labelQuoteNote');
    }
  };

  const getSubmitButtonLabel = () => {
    switch (type) {
      case QuoteNoteType.INTERNAL_NOTE:
        return t('buttonLabelSaveInternalQuoteNote', { entityType });
      case QuoteNoteType.CUSTOMER_NOTE:
        return QuoteType.CUSTOMER === quoteType
          ? t('buttonLabelSaveCustomerQuoteNote', { entityType })
          : t('buttonLabelSaveQuoteNote', { entityType });
    }
  };

  return (
    <>
      <Notification position={NotificationPosition.DRAWER} />

      <Typography level={Level.H4}>{getTabTitle()}</Typography>

      <FormField
        name="content"
        type={InputType.TEXT_EDITOR}
        inputProps={{
          height: 'calc(100vh - 350px)',
          control,
          value: getValues('content'),
          disabled: disabledFields,
          maxLength:
            isAlternateEnabled && QuoteEntityType.ORDER === entityType
              ? NOTE_DEFAULT_MAX_LENGTH
              : undefined,
        }}
      />

      {!disabledFields && (
        <Button
          text={getSubmitButtonLabel()}
          onClick={handleSubmit((data) => onSubmit(data))}
          disabled={!submitEnabled}
        />
      )}

      {confirmationOption && <Confirmation isOpen={true} {...confirmationOption} />}
    </>
  );
};
